import { Button } from '@/components/ui/button'
import { formatPrice } from '@/features/subscribe/format'
import { apiErrorMessage, getStripe } from '@/lib'
import { logger } from '@/ui'
import {
	type BillingProfileData,
	type PaymentMethodData,
	planDataTier,
	planInterval,
	subscriptionCheckoutStatus,
	usePlanIndex,
	useSubscriptionStartTrial,
} from '@jobilla/nexus-sdk'
import { Loader2, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ScreenHeading } from './screen-heading'

type Props = {
	organisationId: string
	profile: BillingProfileData
	card: PaymentMethodData
	onStarted: () => Promise<void>
}

export function FreeTrialScreen({ organisationId, profile, card, onStarted }: Props) {
	const { t, i18n } = useTranslation()
	const startTrial = useSubscriptionStartTrial()
	const [isStarting, setIsStarting] = useState(false)
	const [hasStarted, setHasStarted] = useState(false)
	const [error, setError] = useState<string | null>(null)

	// The trial starts on Lite, so its plan carries the trial length and the price charged afterwards.
	const { data: plans, isPending: isLoadingPlans } = usePlanIndex(organisationId, { profile: profile.id })
	const litePlan = plans?.find((plan) => plan.tier === planDataTier.lite) ?? null
	const litePrice = litePlan?.[planInterval.monthly] ?? null
	const trialDays = litePlan?.trialDays ?? null

	async function start() {
		setIsStarting(true)
		setError(null)

		if (!hasStarted) {
			try {
				const checkout = await startTrial.mutateAsync({
					organisation: organisationId,
					profile: profile.id,
					data: { paymentMethodId: card.id },
				})

				if (checkout.status === subscriptionCheckoutStatus.requires_action) {
					const stripe = checkout.clientSecret ? await getStripe(checkout.publishableKey) : null
					const result = stripe && checkout.clientSecret
						? await stripe.handleNextAction({ clientSecret: checkout.clientSecret })
						: null

					// The trial has started either way. Without 3DS the card may be declined when the trial ends.
					if (result?.error) {
						logger.warn('Card authentication failed after the free trial started', {
							category: 'billing',
							data: { message: result.error.message },
						})
					}
				}

				setHasStarted(true)
			} catch (caught) {
				setError(apiErrorMessage(caught) ?? t('subscribe.confirm.failed'))
				logger.error(caught, { category: 'billing', data: { action: 'start-trial', organisationId } })
				setIsStarting(false)
				return
			}
		}

		try {
			await onStarted()
		} catch (caught) {
			logger.error(caught, { category: 'onboarding', data: { action: 'complete-onboarding', organisationId } })
		} finally {
			setIsStarting(false)
		}
	}

	if (isLoadingPlans) {
		return (
			<div className="flex items-center justify-center py-16 text-muted-foreground">
				<Loader2 className="size-6 animate-spin" />
			</div>
		)
	}

	if (trialDays === null) {
		return (
			<p role="alert" className="text-center text-sm text-destructive">
				{t('onboarding.failed')}
			</p>
		)
	}

	const trialEndsOn = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' })
		.format(new Date(Date.now() + trialDays * 24 * 60 * 60 * 1000))

	return (
		<>
			<ScreenHeading title={t('onboarding.freeTrial.title', { days: trialDays })} />
			<ul className="flex flex-col gap-3 text-base text-muted-foreground">
				<li>
					{t('onboarding.freeTrial.free', { days: trialDays })}
				</li>
				{litePrice && (
					<li>
						{t('onboarding.freeTrial.then', { price: formatPrice(litePrice, i18n.language) })}
					</li>
				)}
				<li>
					{t('onboarding.freeTrial.cancel', { date: trialEndsOn })}
				</li>
			</ul>
			{error && <p role="alert" className="text-center text-sm text-destructive">{error}</p>}
			<Button
				onClick={start}
				disabled={isStarting}
				className="h-14 bg-brand-600 hover:bg-brand-700 text-white"
			>
				{hasStarted ? t('common.tryAgain') : t('onboarding.freeTrial.start')}
			</Button>
			<p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
				<ShieldCheck className="size-3.5" />
				{t('subscribe.confirm.secure')}
			</p>
		</>
	)
}
